import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useGridSettings } from './useGridSettings';
import { arrangeFieldOrder } from '../components/common/DraggableColumnHeader';

/** One column of a hand-written `<Table>` that a chooser can hide or reorder. */
export interface TableColumnDef {
  field: string;
  label: string;
  /** Never offered for hiding (the chooser renders its checkbox disabled). */
  alwaysVisible?: boolean;
}

/**
 * The plain-`<Table>` sibling of `usePersistedDataGrid` (PRX-80 Task 14, R3):
 * the same server-persisted per-user `useGridSettings(gridKey, pharmacyId)`
 * storage and the same `GridSettingEntry` fields (`columnVisibilityModel`,
 * `columnOrder`) — no new storage, no new endpoint, no new preference shape —
 * for tables that are not a DataGrid.
 *
 * The one-shot restore guard per `gridKey` is copied from `usePersistedDataGrid`:
 * saved preferences apply once when they load, then the user owns the state, so
 * a background preferences refetch never yanks the columns back.
 *
 * The guard is gated on `settled`, not on `loaded` (PRX-80 final review, item 7).
 * `loaded` is the preferences read SUCCEEDING, so a failed read left the one-shot
 * armed for the rest of the session: the table showed default columns, said
 * nothing about it, and a later refetch that succeeded would have rearranged the
 * columns under the user's hands. `settled` is success OR error — there is one
 * restore attempt either way, a failed read keeps the defaults (there is nothing
 * to restore), and `loadFailed` lets the chooser say so.
 *
 * `columns` is the NATURAL order and the complete set. A saved order naming a
 * field that no longer exists is ignored; a field the saved order does not name
 * keeps its natural position after the named ones.
 */
export function usePersistedTableColumns(opts: { gridKey: string; pharmacyId?: string; columns: TableColumnDef[] }): {
  orderedColumns: TableColumnDef[];
  /** Every column in the user's order, hidden ones included — what a chooser lists. */
  allColumnsInOrder: TableColumnDef[];
  isVisible: (field: string) => boolean;
  setVisible: (field: string, visible: boolean) => void;
  setOrder: (fields: string[]) => void;
  resetToDefault: () => void;
  loaded: boolean;
  /** The saved view could not be read; these are the default columns. */
  loadFailed: boolean;
} {
  const { gridKey, pharmacyId, columns } = opts;
  const { entry, loaded, settled, save } = useGridSettings(gridKey, pharmacyId);

  const [visibilityModel, setVisibilityModel] = useState<Record<string, boolean>>({});
  const [order, setOrderState] = useState<string[]>([]);
  const restoredKeyRef = useRef<string | null>(null);
  // Whether THIS key's one restore attempt found a saved view or an outage —
  // not a live reading of the query, which a later refetch would flip to
  // "fine" while the table still shows the defaults that failure left behind.
  const [loadFailed, setLoadFailed] = useState(false);
  // The one writer of the visibility model besides React: every path that sets
  // the state assigns the ref in the same tick, so a toggle made before the
  // next render (a reset followed by a toggle, two rapid toggles) composes on
  // the model that is actually current — no mirror effect, no ordering gap.
  const visibilityRef = useRef<Record<string, boolean>>({});

  useEffect(() => {
    if (!settled || restoredKeyRef.current === gridKey) return;
    const isKeySwitch = restoredKeyRef.current !== null;
    restoredKeyRef.current = gridKey;
    setLoadFailed(!loaded);
    if (entry?.columnVisibilityModel) {
      visibilityRef.current = entry.columnVisibilityModel;
      setVisibilityModel(entry.columnVisibilityModel);
    } else if (isKeySwitch) {
      visibilityRef.current = {};
      setVisibilityModel({});
    }
    if (entry?.columnOrder) setOrderState(entry.columnOrder);
    else if (isKeySwitch) setOrderState([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settled, gridKey]);

  const byField = useMemo(() => new Map(columns.map((c) => [c.field, c])), [columns]);

  const isVisible = useCallback(
    (field: string): boolean => {
      const def = byField.get(field);
      if (!def) return false;
      if (def.alwaysVisible) return true;
      return visibilityModel[field] !== false;
    },
    [byField, visibilityModel],
  );

  // `arrangeFieldOrder` is the DataGrid path's own rule (named first, leftovers
  // keep their natural position, deduped) — one implementation for both.
  const allColumnsInOrder = useMemo(
    () => arrangeFieldOrder(columns.map((c) => c.field), order).map((f) => byField.get(f) as TableColumnDef),
    [order, columns, byField],
  );

  const orderedColumns = useMemo(
    () => allColumnsInOrder.filter((c) => isVisible(c.field)),
    [allColumnsInOrder, isVisible],
  );

  const setVisible = useCallback(
    (field: string, visible: boolean) => {
      const def = byField.get(field);
      if (!def || def.alwaysVisible) return;
      // No blur here: on a plain <Table> nothing re-focuses a removed header
      // cell (the GP-1121 hazard is MUI DataGrid's), and blurring would take
      // the keyboard away from the chooser checkbox the user just pressed.
      // No side effect inside the state updater — the next model is computed
      // from the ref so the save fires exactly once per toggle.
      const next = { ...visibilityRef.current, [field]: visible };
      visibilityRef.current = next;
      setVisibilityModel(next);
      save({ columnVisibilityModel: next });
    },
    [byField, save],
  );

  const setOrder = useCallback(
    (fields: string[]) => {
      setOrderState(fields);
      save({ columnOrder: fields });
    },
    [save],
  );

  const resetToDefault = useCallback(() => {
    visibilityRef.current = {};
    setVisibilityModel({});
    setOrderState([]);
    save({ columnVisibilityModel: {}, columnOrder: [] });
  }, [save]);

  return {
    orderedColumns,
    allColumnsInOrder,
    isVisible,
    setVisible,
    setOrder,
    resetToDefault,
    loaded,
    loadFailed,
  };
}

export default usePersistedTableColumns;
